import React, { useState } from 'react';
import { QuickEditAction } from '../types';

interface CustomActionDesignerProps {
  onAddCustomQuickEdit: (action: Omit<QuickEditAction, 'id' | 'promptGenerator' | 'isCustom'> & { promptTemplate: string }) => void;
  existingCustomActions: QuickEditAction[];
}

const CustomActionDesigner: React.FC<CustomActionDesignerProps> = ({ onAddCustomQuickEdit, existingCustomActions }) => {
  const [label, setLabel] = useState('');
  const [promptTemplate, setPromptTemplate] = useState('');
  const [error, setError] = useState<string | null>(null);

  const commonInputClasses = "w-full p-2 rounded-md bg-gray-700 border border-gray-600 focus:ring-indigo-500 focus:border-indigo-500 text-gray-100 text-sm";
  const commonButtonClasses = "px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-md transition duration-150 ease-in-out focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-50 disabled:opacity-50 disabled:cursor-not-allowed text-sm";

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!label.trim()) {
      setError("La Etiqueta de Acción no puede estar vacía.");
      return;
    }
    if (!promptTemplate.trim()) {
      setError("La Plantilla de Prompt no puede estar vacía.");
      return;
    }
    if (!promptTemplate.includes('{baseSubject}')) {
      setError("La Plantilla de Prompt debe incluir el marcador de posición '{baseSubject}'.");
      return;
    }

    onAddCustomQuickEdit({ label, promptTemplate });
    setLabel('');
    setPromptTemplate('');
  };

  return (
    <div className="space-y-6 p-1">
      <div>
        <h3 className="text-lg font-semibold text-gray-200 mb-3">Definir Nueva Edición Personalizada</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="actionLabel" className="block text-sm font-medium text-gray-300 mb-1">
              Etiqueta de Acción:
            </label>
            <input
              type="text"
              id="actionLabel"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              className={commonInputClasses}
              placeholder="Ej: Hacerla festiva"
              aria-describedby="label-desc"
            />
            <p id="label-desc" className="text-xs text-gray-400 mt-1">El nombre que aparecerá en el botón en 'Ediciones Rápidas'.</p>
          </div>
          <div>
            <label htmlFor="promptTemplate" className="block text-sm font-medium text-gray-300 mb-1">
              Plantilla de Prompt:
            </label>
            <textarea
              id="promptTemplate"
              rows={4}
              value={promptTemplate}
              onChange={(e) => setPromptTemplate(e.target.value)}
              className={commonInputClasses + " resize-y"}
              placeholder="Ej: Una versión festiva de {baseSubject} con decoración navideña."
              aria-describedby="template-desc"
            />
            <p id="template-desc" className="text-xs text-gray-400 mt-1">
              Usa <code className="bg-gray-600 px-1 rounded text-indigo-300">{'{baseSubject}'}</code> donde se deba insertar el nombre/descripción de la imagen seleccionada.
            </p>
          </div>
          {error && <p className="text-sm text-red-400">{error}</p>}
          <button type="submit" className={commonButtonClasses}>
            Añadir Edición Personalizada
          </button>
        </form>
      </div>

      {existingCustomActions.length > 0 && (
        <div>
          <h4 className="text-md font-semibold text-gray-200 mb-2 border-t border-gray-700 pt-4">Ediciones Personalizadas Actuales (Solo sesión)</h4>
          <ul className="space-y-2">
            {existingCustomActions.map((action) => (
              <li key={action.id} className="p-3 bg-gray-700 rounded-md shadow">
                <p className="font-semibold text-gray-100">{action.label}</p>
                {/* Fix: Call promptGenerator with a placeholder string for the title attribute, resolving type error and likely subsequent parsing error. */}
                <p className="text-xs text-gray-400 mt-1 truncate" title={action.promptGenerator('{baseSubject}')}>
                  Plantilla: <code className="text-indigo-300">{action.promptGenerator('{baseSubject}')}</code>
                  {/* Note: Displaying the raw template might be better, but this shows the generator works */}
                </p>
              </li>
            ))}
          </ul>
           <p className="text-xs text-gray-500 mt-3">Estas ediciones personalizadas se perderán al refrescar o cerrar la página.</p>
        </div>
      )}
    </div>
  );
};

export default CustomActionDesigner;